import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { QuoteProvider } from "@/context/QuoteContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ProductsList from "@/pages/ProductsList";
import ProductForm from "@/pages/ProductForm";
import Quotes from "@/pages/Quotes";
import Customers from "@/pages/Customers";
import Factories from "@/pages/Factories";
import ProcurementQuotes from "@/pages/ProcurementQuotes";
import ProductOverview from "@/pages/ProductOverview";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import SystemTemplates from "@/pages/SystemTemplates";
import Trash from "@/pages/Trash";
import ResetPassword from "@/pages/ResetPassword";
import ScanProduct from "@/pages/ScanProduct";
import HelpCenter from "@/pages/HelpCenter";
import TagsPage from "@/pages/TagsPage";
import UpcCodes from "@/pages/UpcCodes";
import MarketSurveyPage from "@/pages/MarketSurveyPage";
import ProcurementRequest from "@/pages/ProcurementRequest";
import BadImages from "@/pages/BadImages";
import MarketSharedWall from "@/pages/MarketSharedWall";
import PublicProductLink from "@/pages/PublicProductLink";
import SampleLists from "@/pages/SampleLists";
import SampleListDetail from "@/pages/SampleListDetail";
import PqTemplates from "@/pages/PqTemplates";
import BackupHealth from "@/pages/BackupHealth";

const STAFF = ["admin", "export", "gt_export", "buyer", "production_manager", "sales_manager", "procurement_manager"];
// 2026-08-01 · Roles that can VIEW the Products library page — same as STAFF
// plus the two read-only roles (factory + customer), market_surveyor, and
// the new product_manager (2026-02-27) which is read-only on the library.
const PRODUCTS_VIEW = [...STAFF, "factory", "customer", "market_surveyor", "product_manager"];

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <QuoteProvider>
            <Toaster position="top-right" />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Public role-aware product scan (opened from QR labels — no auth required) */}
              <Route path="/scan/:itemNumber" element={<ScanProduct />} />
              {/* 2026-06 · Public market-survey photo wall (30-day share link) */}
              <Route path="/market/shared/:token" element={<MarketSharedWall />} />
              {/* 2026-06 · Public product share link (normal + ?embed=1 iframe version) */}
              <Route path="/p/:token" element={<PublicProductLink />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<ProtectedRoute roles={PRODUCTS_VIEW}><ProductsList /></ProtectedRoute>} />
                <Route path="products/new" element={<ProtectedRoute roles={STAFF}><ProductForm /></ProtectedRoute>} />
                <Route path="products/:itemNumber/edit" element={<ProtectedRoute roles={[...STAFF, "factory"]}><ProductForm /></ProtectedRoute>} />
                <Route path="quotes" element={<ProtectedRoute roles={["admin", "export", "sales_manager", "customer", "buyer", "procurement_manager"]}><Quotes /></ProtectedRoute>} />
                <Route path="customers" element={<ProtectedRoute roles={["admin", "export", "sales_manager"]}><Customers /></ProtectedRoute>} />
                <Route path="factories" element={<ProtectedRoute roles={["admin", "buyer", "procurement_manager"]}><Factories /></ProtectedRoute>} />
                <Route path="procurement-quotes" element={<ProtectedRoute roles={["admin", "export", "gt_export", "buyer", "procurement_manager"]}><ProcurementQuotes /></ProtectedRoute>} />
                <Route path="pq-templates" element={<ProtectedRoute roles={["admin", "buyer", "procurement_manager"]}><PqTemplates /></ProtectedRoute>} />
                <Route path="backup-health" element={<ProtectedRoute roles={["admin"]}><BackupHealth /></ProtectedRoute>} />
                {/* 采购报价要求链接 — 采购 opens it after logging in (2026-06) */}
                <Route path="procurement-request/:token" element={<ProtectedRoute roles={["admin", "buyer", "procurement_manager"]}><ProcurementRequest /></ProtectedRoute>} />
                <Route path="product-overview" element={<ProtectedRoute roles={["admin", "export", "gt_export", "buyer", "procurement_manager", "sales_manager", "product_manager", "production_manager"]}><ProductOverview /></ProtectedRoute>} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="upc-codes" element={<ProtectedRoute><UpcCodes /></ProtectedRoute>} />
                <Route path="market-survey" element={<MarketSurveyPage />} />
                <Route path="help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
                <Route path="trash" element={<ProtectedRoute roles={["admin", "export"]}><Trash /></ProtectedRoute>} />
                <Route path="bad-images" element={<ProtectedRoute adminOnly><BadImages /></ProtectedRoute>} />
                <Route path="sample-lists" element={<ProtectedRoute><SampleLists /></ProtectedRoute>} />
                <Route path="sample-lists/:id" element={<ProtectedRoute><SampleListDetail /></ProtectedRoute>} />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute adminOnly>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute roles={["admin", "user_admin"]}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="system-templates"
                  element={
                    <ProtectedRoute adminOnly>
                      <SystemTemplates />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Routes>
            </QuoteProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
